import { useEffect, useState } from "react";
import { Loader } from "common/Loader";
import ReactApexChart from "react-apexcharts";
import DatePicker from "react-datepicker";
import UserService from "services/user.service";
import { formatDateY } from "utils/funcs";
import { MetricCard } from "pages/BlackSparrow";

const service = new UserService();

export const DATE = [
  "YESTERDAY",
  "TODAY",
  "WEEKTODATE",
  "LASTWEEK",
  "LASTMONTH",
  "MONTHTODATE",
  "YEARTODATE",
  "CUSTOM",
];

export const WeeklySttats = () => {
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState("YEARTODATE");
  const [showCustom, setShowCustom] = useState(false);
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);
  const [type, setType] = useState('All');
  const [showAllLocations, setShowAllLocations] = useState(false);
  const [stats, setStats] = useState({
    location_wise_training_count: [],
    training_created_in_date_range: [],
    attendess_count_with_in_training: [],
    attendess_count_date_wise: [],
    rsvp_count_date_wise: [],
  });

  useEffect(() => {
    setLoading(true);
    service
      .getWeeklyStats({ type, date })
      .then((resp) => {
        setStats(resp.data);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [date, type]);

  const handleChangeDates = (dates) => {
    const [start, end] = dates;

    setStartDate(start);
    setEndDate(end);

    if ((!start && !end) || end) {
      setDate(`${formatDateY(start)},${formatDateY(end)}`);
    }
  };

  const TOP_N = 15;
  const cleanLocation = (i: any) => i.location && String(i.location).trim() !== "";

  // Real data has 200+ locations, most of them zero. Keep the ones with activity, biggest first.
  const byLocationAll = (stats.attendess_count_with_in_training as any[])
    .filter(cleanLocation)
    .filter((i) => (i.rsvp_count ?? 0) + (i.attendees ?? 0) > 0)
    .sort((a, b) => (b.rsvp_count ?? 0) + (b.attendees ?? 0) - ((a.rsvp_count ?? 0) + (a.attendees ?? 0)));
  const byLocation = showAllLocations ? byLocationAll : byLocationAll.slice(0, TOP_N);

  const byDate = [...(stats.training_created_in_date_range as any[])].sort((a, b) =>
    String(a.date).localeCompare(String(b.date))
  );

  const locationCountsAll = (stats.location_wise_training_count as any[])
    .filter(cleanLocation)
    .filter((i) => (i.total ?? 0) > 0)
    .sort((a, b) => (b.total ?? 0) - (a.total ?? 0));
  const locationCounts = showAllLocations ? locationCountsAll : locationCountsAll.slice(0, TOP_N);

  const totals = {
    trainings: byDate.reduce((s, i) => s + (i.total ?? 0), 0),
    rsvp: byLocationAll.reduce((s, i) => s + (i.rsvp_count ?? 0), 0),
    attendees: byLocationAll.reduce((s, i) => s + (i.attendees ?? 0), 0),
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="page-title">Weekly Training Stats</h1>
          <p className="page-subtitle">RSVPs, attendance and trainings by location and date</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4 mb-4">
        <div className="flex flex-wrap gap-3 [&_input]:h-9 [&_select]:h-9">
          <div className="flex flex-col gap-1 min-w-[180px]">
            <label className="field-label mb-0">Period</label>
            <select
              value={date}
              className="w-full px-3 text-sm"
              onChange={(e) => {
                if (e.target.value !== "CUSTOM") {
                  setDate(e.target.value);
                } else {
                  setShowCustom(true);
                }
              }}
            >
              <option>Select</option>
              {DATE.map((item) => (
                <option value={item} key={item}>
                  {item}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1 min-w-[180px]">
            <label className="field-label mb-0">Type</label>
            <select
              className="w-full px-3 text-sm"
              value={type}
              onChange={e => setType(e.target.value)}
            >
              <option value="">All</option>
              <option value="Weekly Training">Weekly Training</option>
              <option value="Group Interview">Group Interview</option>
            </select>
          </div>

          {showCustom && (
            <div className="flex flex-col gap-1 min-w-[220px]">
              <label className="field-label mb-0">Custom range</label>
              <DatePicker
                selected={startDate}
                placeholderText="Select range"
                onChange={handleChangeDates}
                startDate={startDate}
                endDate={endDate}
                isClearable
                className="w-full"
                customInput={
                  <input
                    placeholder="Select date"
                    className="w-full px-3 text-sm"
                  />
                }
                selectsRange
                selectsDisabledDaysInRange
              />
            </div>
          )}
        </div>
      </div>
      {loading ? (
        <div className="card"><Loader /></div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Headline numbers derived from the same data the charts use */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <MetricCard displayName="Trainings" value={totals.trainings} accent="sky" icon="flag" />
            <MetricCard displayName="RSVPs" value={totals.rsvp} accent="violet" icon="users" />
            <MetricCard displayName="Attendees" value={totals.attendees} accent="emerald" icon="user-check" />
            <MetricCard
              displayName="Active locations"
              value={byLocationAll.length}
              accent="amber"
              icon="chart"
            />
          </div>

          {(byLocationAll.length > TOP_N || locationCountsAll.length > TOP_N) && (
            <div className="flex items-center justify-between -mb-1">
              <p className="text-xs text-ink-muted">
                {showAllLocations
                  ? `Showing all ${Math.max(byLocationAll.length, locationCountsAll.length)} locations`
                  : `Showing the top ${TOP_N} locations by activity`}
              </p>
              <button
                type="button"
                className="btn-ghost btn-sm"
                onClick={() => setShowAllLocations((v) => !v)}
              >
                {showAllLocations ? "Show top 15" : "Show all locations"}
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {/* RSVP vs attendees per location */}
            <ChartCard
              title="RSVP vs. attendees by location"
              subtitle="RSVPs against who actually showed up, biggest locations first"
              empty={byLocation.length === 0}
            >
              <ReactApexChart
                type="bar"
                height={Math.max(260, byLocation.length * 34 + 80)}
                series={[
                  { name: "RSVP", data: byLocation.map((i) => i.rsvp_count ?? 0) },
                  { name: "Attendees", data: byLocation.map((i) => i.attendees ?? 0) },
                ]}
                options={{
                  ...baseChart,
                  colors: ["#a5b4fc", "#0ea5e9"],
                  plotOptions: {
                    bar: { horizontal: true, barHeight: "65%", borderRadius: 3, borderRadiusApplication: "end" },
                  },
                  xaxis: { ...baseChart.xaxis, categories: byLocation.map((i) => i.location) },
                  yaxis: { labels: { style: { fontSize: "12px", colors: "#4b5563" }, maxWidth: 160 } },
                  grid: { ...baseChart.grid, yaxis: { lines: { show: false } }, xaxis: { lines: { show: true } } },
                }}
              />
            </ChartCard>

            {/* Trainings created over time */}
            <ChartCard
              title="Trainings created"
              subtitle={`Trainings created per day · ${byDate.length} days`}
              empty={byDate.length === 0}
            >
              <ReactApexChart
                type="area"
                height={300}
                series={[{ name: "Trainings", data: byDate.map((i) => i.total ?? 0) }]}
                options={{
                  ...baseChart,
                  colors: ["#e11d24"],
                  stroke: { curve: "smooth", width: 2 },
                  fill: { type: "gradient", gradient: { opacityFrom: 0.2, opacityTo: 0.02, stops: [0, 100] } },
                  markers: { size: 3, strokeWidth: 2, strokeColors: "#fff", hover: { size: 5 } },
                  xaxis: {
                    ...baseChart.xaxis,
                    type: "datetime",
                    categories: byDate.map((i) => i.date),
                    labels: { style: { fontSize: "11px" }, datetimeUTC: false, format: "dd MMM" },
                    tooltip: { enabled: false },
                  },
                  yaxis: { labels: { style: { fontSize: "11px" }, formatter: (v: number) => `${Math.round(v)}` } },
                  tooltip: { ...baseChart.tooltip, x: { format: "dd MMM yyyy" } },
                }}
              />
            </ChartCard>

            {/* Trainings per location */}
            <ChartCard
              title="Trainings by location"
              subtitle="How many trainings each location held"
              empty={locationCounts.length === 0}
              className="xl:col-span-2"
            >
              <ReactApexChart
                type="bar"
                height={Math.max(220, locationCounts.length * 30 + 60)}
                series={[{ name: "Trainings", data: locationCounts.map((i) => i.total ?? 0) }]}
                options={{
                  ...baseChart,
                  colors: ["#0ea5e9"],
                  legend: { show: false },
                  plotOptions: {
                    bar: {
                      horizontal: true,
                      barHeight: "60%",
                      borderRadius: 3,
                      borderRadiusApplication: "end",
                      dataLabels: { position: "top" },
                    },
                  },
                  dataLabels: {
                    enabled: true,
                    offsetX: 22,
                    style: { fontSize: "11px", fontWeight: 500, colors: ["#4b5563"] },
                    formatter: (v: number) => `${v}`,
                  },
                  xaxis: { ...baseChart.xaxis, categories: locationCounts.map((i) => i.location) },
                  yaxis: { labels: { style: { fontSize: "12px", colors: "#4b5563" }, maxWidth: 220 } },
                  grid: {
                    ...baseChart.grid,
                    yaxis: { lines: { show: false } },
                    xaxis: { lines: { show: true } },
                    padding: { right: 32 },
                  },
                }}
              />
            </ChartCard>
          </div>
        </div>
      )}
    </div>
  );
};

/* Shared quiet chart chrome for this page */
const baseChart: any = {
  chart: {
    toolbar: { show: false },
    zoom: { enabled: false },
    fontFamily: "Inter, sans-serif",
    foreColor: "#8a919c",
    parentHeightOffset: 0,
    animations: { enabled: false },
  },
  dataLabels: { enabled: false },
  grid: { borderColor: "#eef0f3", strokeDashArray: 3, xaxis: { lines: { show: false } } },
  legend: { position: "top", horizontalAlign: "left", fontSize: "12px", markers: { size: 5 } },
  tooltip: { theme: "light", shared: true, intersect: false },
  xaxis: { axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { fontSize: "11px" } } },
};

const ChartCard = ({
  title,
  subtitle,
  empty,
  className,
  children,
}: {
  title: string;
  subtitle?: string;
  empty?: boolean;
  className?: string;
  children: React.ReactNode;
}) => (
  <div className={`card p-4 min-w-0 ${className ?? ""}`}>
    <p className="card-title">{title}</p>
    {subtitle && <p className="card-subtitle mb-3">{subtitle}</p>}
    {empty ? (
      <div className="flex flex-col items-center justify-center h-48 text-center">
        <p className="text-sm font-medium text-ink-secondary">No data for this period</p>
        <p className="text-xs text-ink-muted mt-0.5">Try a wider period or a different type.</p>
      </div>
    ) : (
      children
    )}
  </div>
);
